import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Returns an array of component rects surrounding weld lines provided from
 * either a rich asset model's component or the component entry itself
 * @param component       The component to pull weld information from
 * @param bufferSize      The distance around welds to use as a buffer
 */
export const useWeldBuffers = (
  component: GeckoComponentModel | undefined,
  bufferSize?: GeckoMeasurement
): ReadonlyArray<GeckoComponentRect> =>
  React.useMemo(() => {
    const result = new Array<GeckoComponentRect>()

    if (
      component &&
      (component.type === GeckoComponentModelType.VerticalTankShell ||
        component.type === GeckoComponentModelType.TankFloor ||
        component.type === GeckoComponentModelType.TankFlatRoof)
    ) {
      component.horizontalWelds?.forEach((weld) => {
        const weldRect = weld.feature_geometry
        // Ignore welds at the bottom of the asset
        if (
          weldRect.y.min
            .minus(component.componentBounds.y.min)
            .abs()
            .toNumber(GeckoMeasurementUnit.Inches) < 6
        ) {
          return
        }
        // Ignore welds at the top of the asset
        if (
          weldRect.y.max
            .minus(component.componentBounds.y.max)
            .abs()
            .toNumber(GeckoMeasurementUnit.Inches) < 6
        ) {
          return
        }

        result.push(
          GeckoComponentRect.CreateFromCoords(
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.start.x,
              weldRect.start.y.minus(bufferSize)
            ),
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.end.x,
              weldRect.end.y.plus(bufferSize)
            )
          )
        )
      })
      component.verticalWelds.forEach((weld) => {
        const weldRect = weld.feature_geometry
        result.push(
          GeckoComponentRect.CreateFromCoords(
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.start.x.minus(bufferSize),
              weldRect.start.y
            ),
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.end.x.plus(bufferSize),
              weldRect.end.y
            )
          )
        )
      })
    }
    return result
  }, [bufferSize, component])
